// Lounge-terrace guest page: /terrace/:spotId — menu with photos, cart, pay at bar or charge to room, live order status.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Plus, Minus, ShoppingBag, Loader2, CheckCircle2, Umbrella, Receipt, Bell, ReceiptText } from 'lucide-react';
import { detectInRoomLang, t, menuName, sectionName, terraceText, callText } from './inroom_i18n';
import { OrderTracker, RecentOrders, rememberOrder } from '../components/inroom/OrderTracker';

const API = process.env.REACT_APP_BACKEND_URL || '';
const eur = (n) => `€${(Number(n || 0)).toFixed(2)}`;

const MenuItem = ({ it, q, bump, lang, T }) => (
  <div className="flex items-center px-4 py-3">
    {it.image_url && <img src={`${API}${it.image_url}`} alt="" loading="lazy" className="w-14 h-14 rounded-xl object-cover mr-3 flex-shrink-0 bg-slate-100" />}
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-slate-900 text-sm">{menuName(lang, it.sku, it.name)}</p>
      <p className="text-xs text-slate-500 mt-0.5">{it.price > 0 ? eur(it.price) : T('complimentary')}</p>
    </div>
    {q === 0 ? (
      <button onClick={() => bump(it.sku, 1)} className="px-3 py-1.5 rounded-full bg-orange-500 text-white text-xs font-semibold hover:bg-orange-600 flex items-center gap-1" data-testid={`terrace-add-${it.sku}`}><Plus className="w-3.5 h-3.5" /> {T('add')}</button>
    ) : (
      <div className="flex items-center gap-2">
        <button onClick={() => bump(it.sku, -1)} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid={`terrace-minus-${it.sku}`}><Minus className="w-4 h-4" /></button>
        <span className="w-6 text-center font-bold" data-testid={`terrace-qty-${it.sku}`}>{q}</span>
        <button onClick={() => bump(it.sku, 1)} className="w-8 h-8 rounded-full bg-orange-500 text-white hover:bg-orange-600 flex items-center justify-center" data-testid={`terrace-plus-${it.sku}`}><Plus className="w-4 h-4" /></button>
      </div>
    )}
  </div>
);

const PayChoice = ({ payment, setPayment, room, setRoom, surname, setSurname, X }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-3" data-testid="terrace-pay">
    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{X('payTitle')}</p>
    <label className={`flex items-center gap-3 rounded-xl border px-3 py-2 mb-2 cursor-pointer ${payment === 'bar' ? 'border-orange-400 bg-orange-50' : 'border-slate-200'}`}>
      <input type="radio" name="pay" checked={payment === 'bar'} onChange={() => setPayment('bar')} data-testid="terrace-pay-bar" /><span className="text-sm text-slate-800">{X('payBar')}</span>
    </label>
    <label className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer ${payment === 'room' ? 'border-orange-400 bg-orange-50' : 'border-slate-200'}`}>
      <input type="radio" name="pay" checked={payment === 'room'} onChange={() => setPayment('room')} data-testid="terrace-pay-room" /><span className="text-sm text-slate-800">{X('payRoom')}</span>
    </label>
    {payment === 'room' && (
      <div className="grid grid-cols-2 gap-2 mt-2">
        <input value={room} onChange={(e) => setRoom(e.target.value)} placeholder={X('roomNumber')} inputMode="numeric" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="terrace-room-number" />
        <input value={surname} onChange={(e) => setSurname(e.target.value)} placeholder={X('surname')} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="terrace-surname" />
      </div>
    )}
  </div>
);

const CallBar = ({ spotId, lang }) => {
  const C = (k) => callText(lang, k);
  const [state, setState] = useState(null); // {type, duplicate}
  const [busy, setBusy] = useState(false);
  const call = async (type) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/terrace/${spotId}/call`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type }) });
      const j = await r.json();
      if (r.ok) { setState({ type, duplicate: j.duplicate }); setTimeout(() => setState(null), 90000); }
    } catch { /* ignore */ } finally { setBusy(false); }
  };
  if (state) {
    return <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="terrace-call-sent"><CheckCircle2 className="w-5 h-5" /> {state.duplicate ? C('already') : C('sent')}</div>;
  }
  return (
    <div className="mb-4 grid grid-cols-2 gap-2" data-testid="terrace-call-bar">
      <button onClick={() => call('waiter')} disabled={busy} className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-800 flex items-center justify-center gap-2 hover:border-orange-300 disabled:opacity-60" data-testid="terrace-call-waiter"><Bell className="w-4 h-4 text-orange-500" /> {C('waiter')}</button>
      <button onClick={() => call('bill')} disabled={busy} className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-800 flex items-center justify-center gap-2 hover:border-orange-300 disabled:opacity-60" data-testid="terrace-call-bill"><ReceiptText className="w-4 h-4 text-orange-500" /> {C('bill')}</button>
    </div>
  );
};

export default function TerracePage() {
  const { spotId } = useParams();
  const [params] = useSearchParams();
  const [lang] = useState(() => params.get('lang') || detectInRoomLang());
  const T = (k) => t(lang, k);
  const X = (k) => terraceText(lang, k);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [cart, setCart] = useState({});
  const [note, setNote] = useState('');
  const [guestName, setGuestName] = useState('');
  const [payment, setPayment] = useState('bar');
  const [room, setRoom] = useState('');
  const [surname, setSurname] = useState('');
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/terrace/${spotId}?lang=${lang}`).then(async (r) => { if (!r.ok) throw new Error(X('notFound')); setData(await r.json()); }).catch((e) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotId, lang]);

  const allItems = useMemo(() => Object.values(data?.menu || {}).flat(), [data]);
  const bump = (sku, d) => setCart((c) => { const n = Math.max(0, (c[sku] || 0) + d); const next = { ...c }; if (n) next[sku] = n; else delete next[sku]; return next; });
  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const total = Object.entries(cart).reduce((sum, [sku, q]) => sum + q * (allItems.find((i) => i.sku === sku)?.price || 0), 0);

  const submit = async () => {
    setPlacing(true); setErr('');
    try {
      const body = { items: Object.entries(cart).map(([sku, qty]) => ({ sku, qty })), note, guest_name: guestName, lang, payment, room_number: room || null, surname: surname || null };
      const r = await fetch(`${API}/api/terrace/${spotId}/order`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error');
      rememberOrder(j.order.id);
      setDone(j);
    } catch (e) { setErr(e.message); } finally { setPlacing(false); }
  };

  if (err && !data) return <div className="min-h-screen flex items-center justify-center text-slate-600 p-6 text-center" data-testid="terrace-error">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>;

  const spot = data.spot;
  const spotLabel = `${X(spot.type)} ${spot.number}`;
  const menuEntries = Object.entries(data.menu);
  const sections = menuEntries.map(([section, arr]) => {
    const rows = arr.map((it) => <MenuItem key={it.sku} it={it} q={cart[it.sku] || 0} bump={bump} lang={lang} T={T} />);
    return (
      <section key={section} className="mb-5">
        <h2 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2 px-1">{sectionName(lang, section)}</h2>
        <div className="bg-white rounded-2xl border border-slate-200 divide-y">{rows}</div>
      </section>
    );
  });

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="terrace-page">
      <header className="bg-slate-900 text-white px-5 pt-8 pb-6">
        <p className="text-[11px] uppercase tracking-widest text-orange-300">{X('kicker')}</p>
        <div className="flex items-center justify-between gap-3 mt-1">
          <h1 className="text-3xl font-bold" data-testid="terrace-spot">{spotLabel}</h1>
          <span className="inline-flex items-center gap-1 bg-white/10 rounded-full px-3 py-1 text-sm"><Umbrella className="w-4 h-4" /> {spot.id}</span>
        </div>
        <p className="text-sm text-slate-300 mt-2">{X('greeting')}</p>
      </header>
      <div className="max-w-2xl mx-auto px-4 mt-5">
        {done ? (
          <div className="bg-white rounded-2xl border border-green-200 p-6 text-center" data-testid="terrace-success">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
            <h2 className="text-xl font-bold text-slate-900">{T('orderSuccess')}</h2>
            <p className="text-sm text-slate-600 mt-1">{X('successBody')(done.order.items.length, eur(done.order.total_eur), spotLabel)}</p>
            {done.attached_to_folio && <p className="mt-3 text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg px-3 py-2 inline-flex items-center gap-2" data-testid="terrace-room-matched"><Receipt className="w-4 h-4" /> {X('roomMatched')(done.order.room_charge?.room_number)}</p>}
            {done.room_charge === 'room_not_matched' && <p className="mt-3 text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded-lg px-3 py-2" data-testid="terrace-room-unmatched">{X('roomNotMatched')}</p>}
            <div className="mt-4 text-left"><OrderTracker orderId={done.order.id} lang={lang} /></div>
            <button onClick={() => { setDone(null); setCart({}); setNote(''); }} className="mt-4 text-sm font-semibold text-orange-600" data-testid="terrace-again">{T('sendAnother')}</button>
          </div>
        ) : (
          <>
            <CallBar spotId={spot.id} lang={lang} />
            <RecentOrders lang={lang} />
            {sections}
            {cartCount > 0 && (
              <>
                <input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder={T('yourName')} className="w-full rounded-lg border border-slate-200 px-4 py-2 mb-3 text-sm" data-testid="terrace-guest-name" />
                <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={T('orderNote')} rows={2} className="w-full rounded-lg border border-slate-200 px-4 py-2 mb-3 text-sm" data-testid="terrace-note" />
                <PayChoice payment={payment} setPayment={setPayment} room={room} setRoom={setRoom} surname={surname} setSurname={setSurname} X={X} />
                {err && <p className="text-sm text-red-600 mb-2" data-testid="terrace-submit-error">{err}</p>}
                <div className="fixed bottom-4 left-4 right-4 max-w-2xl mx-auto z-20">
                  <button onClick={submit} disabled={placing || (payment === 'room' && (!room || !surname))} className="w-full py-4 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-2xl flex items-center justify-center gap-3 disabled:opacity-60" data-testid="terrace-submit">
                    {placing ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShoppingBag className="w-5 h-5" />}
                    {T('placeOrder')} · {cartCount} {cartCount > 1 ? T('items') : T('item')} · {eur(total)} · {spotLabel}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
